import { Link, useParams } from "react-router-dom";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { resolveDepartment } from "../lib/departments";
import { usePublishedAlumnus } from "../lib/publishedAlumni";
import { useScrollPastHero } from "../lib/useScrollPastHero";

/**
 * One of a department's published alumni (see publishedAlumni.ts): only the
 * fields webadmin publishes for them, exactly as published. Nothing comes from
 * the Alumni page's own catalogue, even where it has someone of the same name.
 */
export default function DepartmentAlumnusProfile() {
  const { slug = "", alumnus = "" } = useParams<{ slug: string; alumnus: string }>();
  const d = resolveDepartment(slug);
  const { loading, departmentName, alumnus: a, others } = usePublishedAlumnus(d?.code, alumnus);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (loading) return null;

  const deptPath = d ? `${ROUTES.department}/${d.slug}` : ROUTES.home;

  if (!a || !departmentName) {
    return (
      <section className="alum-hero">
        <div className="container">
          <div className="ah-eyebrow">404 · Alumni</div>
          <h1 className="ah-name">Profile not found.</h1>
          <p>
            <Link to={deptPath}>← Back to the department</Link>
          </p>
        </div>
      </section>
    );
  }

  // The published quote, in its own paragraphs.
  const paragraphs = a.quote.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={a.graduationYear ? `CLASS OF ${a.graduationYear}` : departmentName.toUpperCase()}
        title={a.name}
        back={{ label: departmentName, to: deptPath }}
      />
      <section className="alum-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb" style={{ color: "var(--text-muted)" }}>
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={deptPath}>{departmentName}</Link>
            <span aria-hidden>/</span>
            <span>Distinguished alumni</span>
          </nav>

          <div className="ah-grid">
            <div className="ah-portrait">
              <Figure media={a.photo} fallbackLabel={"PORTRAIT · " + a.name.toUpperCase()} aspect="3/4" />
            </div>
            <div>
              <div className="ah-eyebrow">
                {a.graduationYear ? `Class of ${a.graduationYear} · ` : ""}
                {departmentName}
              </div>
              <h1 className="ah-name">{a.name}</h1>
              {(a.currentRole || a.organization) && (
                <div className="ah-role">
                  {a.currentRole}
                  {a.organization && <span className="ah-org">{a.organization}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              {paragraphs.length > 0 ? (
                paragraphs.map((p, i) => <p key={i}>{p}</p>)
              ) : (
                <p style={{ color: "var(--text-muted)" }}>No profile text published yet.</p>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              {a.graduationYear && (
                <div className="dmc-block">
                  <div className="dmc-label">Class of</div>
                  <div className="dmc-value">{a.graduationYear}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Department</div>
                <div className="dmc-value">
                  <Link to={deptPath}>{departmentName}</Link>
                </div>
              </div>
              {a.currentRole && (
                <div className="dmc-block">
                  <div className="dmc-label">Current role</div>
                  <div className="dmc-value">{a.currentRole}</div>
                </div>
              )}
              {a.organization && (
                <div className="dmc-block">
                  <div className="dmc-label">Organization</div>
                  <div className="dmc-value">{a.organization}</div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Every other alumnus the department publishes — the department page
          shows four, so this is how the rest are reached. */}
      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">Distinguished alumni</div>
              <h2>More from {departmentName}</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${deptPath}/alumni/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.graduationYear ? `'${String(o.graduationYear).slice(2)}` : "—"}</span>
                    <span className="er-month">CLASS</span>
                  </div>
                  <div className="er-body">
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.currentRole}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
